// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CmsEditingMode from "@hardwoods/App/Components/CMS/CmsEditingMode";
import { constants } from "@hardwoods/XDL/constants";
import { EventBus } from "@hardwoods/XDL/Utilities";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { FC, useEffect, useRef } from "react";
import { css } from "styled-components";

export interface FooterContainerStyles {
    widgetGridContainerTop?: GridContainerProps;
    widgetGridContainerBottom?: GridContainerProps;
    productsGridItem?: GridItemProps;
    logoGridItem?: GridItemProps;
    companyGridItem?: GridItemProps;
    resourcesGridItem?: GridItemProps;
    contentGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    secureGridItem?: GridItemProps;
}
interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

type Props = OwnProps & HasShellContext;

export const headingFooterStyles = css`
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: bold;
    text-transform: uppercase;
    transform: translateY(-5px);
    color: ${color("common.white")};
    line-height: 22px;
    letter-spacing: 0.5px;
`;

const linkStyles = css`
    color: ${color("common.brand4")};
    margin-bottom: 18px;
    font-size: 12px;
    text-transform: capitalize;

    &:hover {
        color: ${color("common.brand4")};
        text-decoration: underline;

        span {
            color: ${color("common.brand4")};
        }
    }

    span {
        color: ${color("common.brand4")};
    }
`;

const linkListStyles = css`
    margin-bottom: 40px;
    color: ${color("common.brand4")};

    ${xScreen.lte("sm")} {
        margin-bottom: 20px;
    }

    h3 {
        ${headingFooterStyles}
    }

    a.link {
        ${linkStyles}
    }

    .linkList-component {
        padding: 0;
        h3 {
            ${headingFooterStyles}
        }
        .linkList-linkItem-wrapper {
            a {
                ${linkStyles}
            }
            a:hover > span {
                color: ${color("common.brand4")};
            }
        }
    }
`;

const maxWidthMediaQueriesStyle = css`
    ${({ theme }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return css`
            ${breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    max-width: ${maxWidths[2]}px;
                    margin: 0 auto;
                `,
                css`
                    max-width: ${maxWidths[3]}px;
                    margin: 0 auto;
                `,
                css`
                    max-width: ${maxWidths[4]}px;
                    margin: 0 auto;
                `,
            ])}
        `;
    }}
`;

export const footerContainerStyles: FooterContainerStyles = {
    widgetGridContainerTop: {
        css: css`
            background-color: transparent;
            padding: 0 20px 10px;
            ${xScreen.lte("xs")} {
                padding-bottom: 0;
            }

            ${maxWidthMediaQueriesStyle}
        `,
    },
    widgetGridContainerBottom: {
        css: css`
            background-color: transparent;
            padding: 0 20px;
            ${maxWidthMediaQueriesStyle}
        `,
    },
    logoGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            color: ${color("common.white")};

            ${xScreen.lte("sm")} {
                justify-content: center;
                text-align: center;
            }
        `,
    },
    companyGridItem: {
        width: [12, 6, 6, 2, 2],
        css: css`
            ${linkListStyles}
        `,
    },
    productsGridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            color: ${color("common.white")};
            transition: max-width 350ms ease-in-out;
            &.hide {
                &&& {
                    max-width: none;
                    width: 0;
                    flex: 0;
                    visibility: hidden;
                    padding: 0;
                    height: 0;
                    overflow: hidden;
                }
            }

            .linkList-component {
                padding: 0;
                display: flex;
                height: 235px;
                flex-flow: column wrap;
                align-content: flex-start;
                
                ${xScreen.lte("xs")} {
                    height: auto;
                    flex-flow: column nowrap;
                }

                .linkList-linkItem-wrapper {
                    width: 180px;
                    justify-content: flex-start;
                    &:nth-of-type(7) {
                        margin-top: 32px;
                        ${xScreen.lte("xs")} {
                            margin-top: 0;
                        }
                    }
                }
            }

            h3 {
                ${headingFooterStyles}
            }

            a[class*="StyledButton"] {
                ${linkStyles}
            }

            .featureBrands > div {
                display: flex;
                height: 220px;
                flex-flow: column wrap;
                align-content: flex-start;

                div {
                    width: 180px;
                    justify-content: flex-start;
                }
            }

            ${xScreen.lte("sm")} {
                order: 4;
                h3 {
                    transform: none;
                }
            }

            ${xScreen.lte("xs")} {
                div > div {
                    div {
                        width: 100%;
                    }
                }
                .featureBrands > div {
                    height: auto;
                    flex-flow: column nowrap;
                }
            }
        `,
    },
    resourcesGridItem: {
        width: [12, 6, 6, 2, 2],
        css: css`
            ${linkListStyles}
        `,
    },
    emptyGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            ${linkListStyles}
        `,
    },
    subscribeGridItem: {
        width: [12, 12, 12, 10, 10],
        css: css`
            color: ${color("common.brand4")};

            h3 {
                ${headingFooterStyles}
                transform: translateY(-5px);
            }

            .socialLinkList {
                margin-bottom: 50px;

                .linkWrapper {
                    flex-basis: auto;
                    flex-shrink: 1;
                    padding: 0 10px 0 0;
                }
            }
        `,
    },
    secureGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            ${linkListStyles}
            align-items: flex-end;

            ${xScreen.lte("sm")} {
                text-align: center;
            }
        `,
    },
};

const FooterContainer: FC<Props> = ({ id, shellContext, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const [productGridItemsWidth, setProductGridItemsWidth] = React.useState<GridWidths | GridWidths[]>([
        12,
        6,
        6,
        2,
        2,
    ]);
    const [hideProductGridItem, setHideProductGridItem] = React.useState<boolean>(false);
    const productRef = useRef<HTMLDivElement>(null);

    const updateProductGridItemWidth = async () => {
        const elements = productRef.current?.querySelectorAll(".featureBrandsList, .linkList-component");
        if (!elements || elements.length === 0) {
            setHideProductGridItem(true);
            return;
        }

        const element = elements[0];
        await waitFor(() => element.children.length > 0, 10000);
        if (element.children.length === 0) {
            setHideProductGridItem(true);
            return;
        }

        let heightItems = 0;
        for (const child of element.children) {
            heightItems += (child as HTMLElement).clientHeight;
        }
        setHideProductGridItem(false);

        if (heightItems > element.clientHeight || (element.children && element.children.length > 5)) {
            setProductGridItemsWidth([12, 12, 6, 4, 4]);
        } else {
            setProductGridItemsWidth([12, 6, 6, 2, 2]);
        }
    };

    useEffect(() => {
        EventBus.on(constants.customEvents.footerFeatureBrands, (data: any) => {
            updateProductGridItemWidth();
        });
        updateProductGridItemWidth();
    }, []);

    return (
        <>
            <GridContainer {...styles.widgetGridContainerTop}>
                <GridItem {...styles.logoGridItem}>
                    <CmsEditingMode editingChildren="Zone for Logo and Info">
                        <Zone zoneName="FooterLogoZone" contentId={id} />
                    </CmsEditingMode>
                </GridItem>
                <GridItem {...styles.companyGridItem}>
                    <CmsEditingMode editingChildren="Zone for Company Links">
                        <Zone zoneName="FooterCompanyZone" contentId={id} />
                    </CmsEditingMode>
                </GridItem>
                <GridItem
                    {...styles.productsGridItem}
                    width={productGridItemsWidth}
                    className={`${hideProductGridItem && !shellContext.isEditing ? "hide" : "show"}`}
                >
                    <StyledWrapper style={{ width: "100%" }} ref={productRef}>
                        <CmsEditingMode editingChildren="Zone for Featured Brands">
                            <Zone zoneName="FooterProductZone" contentId={id} />
                        </CmsEditingMode>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.resourcesGridItem}>
                    <CmsEditingMode editingChildren="Zone for Resources links">
                        <Zone zoneName="FooterResourcesZone" contentId={id} />
                    </CmsEditingMode>
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.widgetGridContainerBottom}>
                <GridItem {...styles.subscribeGridItem}>
                    <CmsEditingMode editingChildren="Zone for Subscribe component">
                        <Zone zoneName="FooterSubscribeZone" contentId={id} />
                    </CmsEditingMode>
                </GridItem>
                <GridItem {...styles.secureGridItem}>
                    <CmsEditingMode editingChildren="Zone for secure component">
                        <Zone zoneName="FooterSecureLogoZone" contentId={id} />
                    </CmsEditingMode>
                </GridItem>
            </GridContainer>
        </>
    );
};

const footerContainer: WidgetModule = {
    component: withIsInShell(FooterContainer),
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
